import React, { useState, useEffect } from 'react';
import { withTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Alert,
  Paper,
  Link,
  Tab,
  Tabs,
  Grid,
  TextField,
  Snackbar,
  IconButton,
  Card,
  CardContent,
  Divider,
  Stack,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Add as AddIcon,
  Remove as RemoveIcon,
  SwapVert as SwapVertIcon,
  Inventory as InventoryIcon,
  Undo as UndoIcon,
  Delete as DeleteIcon,
  PlayArrow as PlayArrowIcon,
  Launch as LaunchIcon,
  InfoOutlined as InfoOutlinedIcon,
  Settings as SettingsIcon,
  History as HistoryIcon,
  Close as CloseIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { useUserStore } from 'store';
import DecimalField from '../shared/DecimalField';
import { formatQuantity, formatQuantityChange } from '../../utils';
import UnitSuffix from '../shared/UnitSuffix';

// Tab Panel component (same pattern as VehicleDetailsDialog)
function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`inventory-item-tabpanel-${index}`}
      aria-labelledby={`inventory-item-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 2 }}>{children}</Box>}
    </div>
  );
}

const transactionTypeConfig = {
  initial: { color: 'default', icon: PlayArrowIcon },
  purchase: { color: 'success', icon: AddIcon },
  sale: { color: 'error', icon: RemoveIcon },
  adjustment: { color: 'warning', icon: SwapVertIcon },
  count: { color: 'info', icon: InventoryIcon },
  return: { color: 'success', icon: UndoIcon },
  damaged: { color: 'error', icon: DeleteIcon },
  parts_sale: { color: 'error', icon: RemoveIcon },
};

// --- Reservations Section ---
function ReservationsSection({ t, inventoryItem }) {
  const { currentOrganization } = useUserStore();
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (inventoryItem) {
      fetchReservations();
    }
  }, [inventoryItem?.id]);

  const fetchReservations = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `/api/inventory/${inventoryItem.id}/reservations/?organization=${currentOrganization.organization_id}`
      );
      setReservations(response.data);
    } catch (err) {
      console.error('Failed to fetch reservations:', err);
    } finally {
      setLoading(false);
    }
  };

  const getStateLabel = (state) => {
    return t(state) || state;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
        <CircularProgress size={20} />
      </Box>
    );
  }

  if (reservations.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        {t('inventory.noReservations')}
      </Typography>
    );
  }

  return (
    <TableContainer component={Paper} variant="outlined" sx={{ overflowX: 'auto' }}>
      <Table size="small" sx={{ minWidth: 500 }}>
        <TableHead>
          <TableRow>
            <TableCell>{t('inventory.maintenanceLink')}</TableCell>
            <TableCell>{t('Vehicle')}</TableCell>
            <TableCell>{t('Customer')}</TableCell>
            <TableCell align="right">{t('inventory.quantity')}</TableCell>
            <TableCell>{t('Status')}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {reservations.map((res, idx) => (
            <TableRow key={idx} hover>
              <TableCell>
                <Link
                  component={RouterLink}
                  to={`/maintenances/${res.maintenance_id}`}
                  sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
                >
                  {res.work_number}
                  <LaunchIcon sx={{ fontSize: 14 }} />
                </Link>
              </TableCell>
              <TableCell>
                <Typography variant="body2">
                  {res.vehicle}
                  {res.registration_number && (
                    <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 0.5 }}>
                      ({res.registration_number})
                    </Typography>
                  )}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="body2">{res.customer_name || '-'}</Typography>
              </TableCell>
              <TableCell align="right">
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {formatQuantity(res.quantity)}
                </Typography>
              </TableCell>
              <TableCell>
                <Chip size="small" label={getStateLabel(res.state)} />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

// --- Overview Tab ---
function OverviewTab({ t, inventoryItem }) {
  const formatCurrency = (value) => {
    if (value === null || value === undefined) return '-';
    return new Intl.NumberFormat('fi-FI', {
      style: 'currency',
      currency: 'EUR',
    }).format(parseFloat(value));
  };
  // The part's unit (kpl, l, kg): per-unit prices read "10,76 €/l", the stock
  // value stays a plain amount.
  const unit = inventoryItem?.part_unit || 'kpl';
  const formatPerUnit = (value) => (value === null || value === undefined ? '-' : `${formatCurrency(value)}/${unit}`);

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Intl.DateTimeFormat('fi-FI', { dateStyle: 'short' }).format(new Date(dateStr));
  };

  const item = inventoryItem;

  return (
    <Box>
      {/* Stock Levels */}
      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5, fontWeight: 600 }}>
        {t('inventory.stockLevels')}
      </Typography>
      <Grid container spacing={1.5} sx={{ mb: 3 }}>
        <Grid item xs={4}>
          <Paper variant="outlined" sx={{ p: 1.5, textAlign: 'center', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
            <Typography variant="caption" color="text.secondary">{t('inventory.currentStock')}</Typography>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 700,
                color: parseFloat(item.quantity_in_stock) < 0 ? 'error.main' : 'text.primary',
              }}
            >
              {formatQuantity(item.quantity_in_stock)}
              <UnitSuffix unit={unit} />
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={4}>
          <Paper variant="outlined" sx={{ p: 1.5, textAlign: 'center', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
            <Typography variant="caption" color="text.secondary">{t('inventory.reservedStock')}</Typography>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              {formatQuantity(item.quantity_reserved)}
              <UnitSuffix unit={unit} />
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={4}>
          <Paper variant="outlined" sx={{ p: 1.5, textAlign: 'center', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
            <Typography variant="caption" color="text.secondary">{t('inventory.freeStock')}</Typography>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 700,
                color: item.is_low_stock ? 'warning.main' : 'text.primary',
              }}
            >
              {formatQuantity(item.quantity_free)}
              <UnitSuffix unit={unit} />
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* On Order */}
      {parseFloat(item.quantity_on_order) > 0 && (
        <Paper variant="outlined" sx={{ p: 1.5, mb: 2, display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Typography variant="caption" color="text.secondary">{t('inventory.onOrder')}:</Typography>
          <Typography variant="body1" sx={{ fontWeight: 600, color: 'info.main' }}>
            {formatQuantity(item.quantity_on_order)}
          </Typography>
          <Typography variant="caption" color="text.secondary">{t('inventory.onOrderTooltip')}</Typography>
        </Paper>
      )}

      {/* Cost & Value */}
      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5, fontWeight: 600 }}>
        {t('inventory.costAndValue')} <Typography component="span" sx={{ fontSize: '12px', fontWeight: 400 }}>({t('excl. VAT')})</Typography>
      </Typography>
      <Grid container spacing={1.5} sx={{ mb: 3 }}>
        <Grid item xs={4}>
          <Paper variant="outlined" sx={{ p: 1.5, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', textAlign: 'center' }}>
            <Typography variant="caption" color="text.secondary">{t('inventory.avgUnitCost')}</Typography>
            <Typography variant="body1" sx={{ fontWeight: 600 }}>
              {formatPerUnit(item.avg_unit_cost)}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={4}>
          <Paper variant="outlined" sx={{ p: 1.5, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', textAlign: 'center' }}>
            <Typography variant="caption" color="text.secondary">{t('inventory.stockValue')}</Typography>
            <Typography variant="body1" sx={{ fontWeight: 600 }}>
              {formatCurrency(item.stock_value)}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={4}>
          <Paper variant="outlined" sx={{ p: 1.5, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', textAlign: 'center' }}>
            <Typography variant="caption" color="text.secondary">{t('inventory.catalogPrice')}</Typography>
            <Typography variant="body1" sx={{ fontWeight: 600 }}>
              {formatPerUnit(item.part_price)}
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Details */}
      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5, fontWeight: 600 }}>
        {t('inventory.details')}
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={6} sm={4}>
          <Typography variant="body2" color="text.secondary">{t('inventory.shelfLocation')}</Typography>
          <Typography variant="body1">{item.shelf_location || '-'}</Typography>
        </Grid>
        <Grid item xs={6} sm={4}>
          <Typography variant="body2" color="text.secondary">{t('inventory.reorderPoint')}</Typography>
          <Typography variant="body1">{item.reorder_point ? formatQuantity(item.reorder_point) : '-'}</Typography>
        </Grid>
        <Grid item xs={6} sm={4}>
          <Typography variant="body2" color="text.secondary">{t('inventory.reorderQuantity')}</Typography>
          <Typography variant="body1">{item.reorder_quantity ? formatQuantity(item.reorder_quantity) : '-'}</Typography>
        </Grid>
        <Grid item xs={6} sm={4}>
          <Typography variant="body2" color="text.secondary">{t('inventory.manufacturerCode')}</Typography>
          <Typography variant="body1">{item.part_primary_code || item.part_manufacturer_code || '-'}</Typography>
        </Grid>
        <Grid item xs={6} sm={4}>
          <Typography variant="body2" color="text.secondary">{t('inventory.lastCountDate')}</Typography>
          <Typography variant="body1">{formatDate(item.last_count_date)}</Typography>
        </Grid>
        <Grid item xs={6} sm={4}>
          <Typography variant="body2" color="text.secondary">{t('inventory.lastCountQuantity')}</Typography>
          <Typography variant="body1">{item.last_count_quantity ? formatQuantity(item.last_count_quantity) : '-'}</Typography>
        </Grid>
      </Grid>

      {/* Reserving Maintenances */}
      {parseFloat(item.quantity_reserved) > 0 && (
        <>
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5, mt: 3, fontWeight: 600 }}>
            {t('inventory.reservingMaintenances')}
          </Typography>
          <ReservationsSection t={t} inventoryItem={item} />
        </>
      )}
    </Box>
  );
}

// --- Settings Tab ---
function SettingsTab({ t, inventoryItem, onSaved }) {
  const { currentOrganization } = useUserStore();
  const [shelfLocation, setShelfLocation] = useState(inventoryItem.shelf_location || '');
  const [reorderPoint, setReorderPoint] = useState(inventoryItem.reorder_point || '');
  const [reorderQuantity, setReorderQuantity] = useState(inventoryItem.reorder_quantity || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Reset form when inventoryItem changes
  useEffect(() => {
    setShelfLocation(inventoryItem.shelf_location || '');
    setReorderPoint(inventoryItem.reorder_point || '');
    setReorderQuantity(inventoryItem.reorder_quantity || '');
  }, [inventoryItem.id, inventoryItem.shelf_location, inventoryItem.reorder_point, inventoryItem.reorder_quantity]);

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);

      await axios.patch(
        `/api/inventory/${inventoryItem.id}/update_settings/?organization=${currentOrganization.organization_id}`,
        {
          shelf_location: shelfLocation,
          reorder_point: reorderPoint || null,
          reorder_quantity: reorderQuantity || null,
        }
      );

      onSaved();
    } catch (err) {
      console.error('Failed to save settings:', err);
      setError(t('inventory.settingsSaveError'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
      )}
      <Grid container spacing={3}>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label={t('inventory.shelfLocation')}
            value={shelfLocation}
            onChange={(e) => setShelfLocation(e.target.value)}
            placeholder="A-12-3"
            helperText={t('inventory.shelfLocationHelp')}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          {/* Spacer for layout */}
        </Grid>
        <Grid item xs={12} sm={6}>
          <DecimalField
            fullWidth
            label={t('inventory.reorderPoint')}
            value={reorderPoint}
            onChange={setReorderPoint}
            helperText={t('inventory.reorderPointHelp')}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <DecimalField
            fullWidth
            label={t('inventory.reorderQuantity')}
            value={reorderQuantity}
            onChange={setReorderQuantity}
            helperText={t('inventory.reorderQuantityHelp')}
          />
        </Grid>
        <Grid item xs={12}>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving}
            data-testid="inventory-settings-save"
          >
            {saving ? <CircularProgress size={20} sx={{ mr: 1 }} /> : null}
            {t('inventory.saveSettings')}
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
}

// --- Transaction Detail Dialog ---
function TransactionDetailDialog({ t, open, onClose, transaction }) {
  if (!transaction) return null;

  const config = transactionTypeConfig[transaction.transaction_type] || { color: 'default' };
  const Icon = config.icon;
  const change = parseFloat(transaction.quantity_change);

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-';
    return new Intl.DateTimeFormat('fi-FI', {
      dateStyle: 'short',
      timeStyle: 'short',
    }).format(new Date(dateStr));
  };

  const formatCurrency = (value) => {
    if (value === null || value === undefined) return null;
    return new Intl.NumberFormat('fi-FI', {
      style: 'currency',
      currency: 'EUR',
    }).format(value);
  };

  const getTransactionTypeLabel = (type) => {
    return t(`inventory.transactionTypes.${type}`, { defaultValue: type });
  };

  const InfoBox = ({ label, children }) => (
    <Paper variant="outlined" sx={{ p: 1.5, height: '100%' }}>
      <Typography variant="caption" color="text.secondary">{label}</Typography>
      <Box sx={{ mt: 0.5 }}>{children}</Box>
    </Paper>
  );

  const quantityBefore = transaction.quantity_after !== null && transaction.quantity_after !== undefined
    ? parseFloat(transaction.quantity_after) - parseFloat(transaction.quantity_change)
    : null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Typography variant="h6" component="span">{t('inventory.transactionDetails')}</Typography>
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {/* Transaction info */}
        <Grid container spacing={1.5} sx={{ mb: 2 }}>
          <Grid item xs={6}>
            <InfoBox label={t('Type')}>
              <Chip
                size="small"
                label={getTransactionTypeLabel(transaction.transaction_type)}
                color={config.color}
                icon={Icon ? <Icon /> : undefined}
              />
            </InfoBox>
          </Grid>
          <Grid item xs={6}>
            <InfoBox label={t('Date')}>
              <Typography variant="body2">{formatDateTime(transaction.created_at)}</Typography>
            </InfoBox>
          </Grid>
          <Grid item xs={4}>
            <InfoBox label={t('Change')}>
              <Typography
                variant="body1"
                sx={{ fontWeight: 600, color: change >= 0 ? 'success.main' : 'error.main' }}
              >
                {formatQuantityChange(transaction.quantity_change)}
              </Typography>
            </InfoBox>
          </Grid>
          {quantityBefore !== null && (
            <Grid item xs={4}>
              <InfoBox label={t('inventory.quantityBefore')}>
                <Typography variant="body2">{formatQuantity(quantityBefore)}</Typography>
              </InfoBox>
            </Grid>
          )}
          <Grid item xs={4}>
            <InfoBox label={t('After')}>
              <Typography variant="body2">{formatQuantity(transaction.quantity_after)}</Typography>
            </InfoBox>
          </Grid>
        </Grid>

        {/* Reference section - only if any reference fields exist */}
        {(transaction.maintenance_id || transaction.reference_number || transaction.invoice_number || transaction.vendor_name) && (
          <>
            <Divider sx={{ my: 2 }} />
            <Grid container spacing={1.5}>
              {transaction.maintenance_id && (
                <Grid item xs={6}>
                  <InfoBox label={t('inventory.maintenanceLink')}>
                    <Link
                      component={RouterLink}
                      to={`/maintenances/${transaction.maintenance_id}`}
                      sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
                    >
                      {transaction.maintenance_work_number}
                      <LaunchIcon sx={{ fontSize: 14 }} />
                    </Link>
                  </InfoBox>
                </Grid>
              )}
              {transaction.reference_number && (
                <Grid item xs={6}>
                  <InfoBox label={t('inventory.referenceNumber')}>
                    <Typography variant="body2">{transaction.reference_number}</Typography>
                  </InfoBox>
                </Grid>
              )}
              {transaction.invoice_number && (
                <Grid item xs={6}>
                  <InfoBox label={t('inventory.invoiceNumber')}>
                    <Typography variant="body2">{transaction.invoice_number}</Typography>
                  </InfoBox>
                </Grid>
              )}
              {transaction.vendor_name && (
                <Grid item xs={6}>
                  <InfoBox label={t('inventory.vendor')}>
                    <Typography variant="body2">{transaction.vendor_name}</Typography>
                  </InfoBox>
                </Grid>
              )}
            </Grid>
          </>
        )}

        {/* Pricing section - only if any pricing fields exist */}
        {(transaction.unit_cost || transaction.cogs_unit_cost || transaction.selling_price) && (
          <>
            <Divider sx={{ my: 2 }} />
            <Grid container spacing={1.5}>
              {transaction.unit_cost && (
                <Grid item xs={4}>
                  <InfoBox label={t('inventory.purchaseCost')}>
                    <Typography variant="body2">{formatCurrency(transaction.unit_cost)}</Typography>
                  </InfoBox>
                </Grid>
              )}
              {transaction.cogs_unit_cost && (
                <Grid item xs={4}>
                  <InfoBox label={t('inventory.cogsUnitCost')}>
                    <Typography variant="body2">{formatCurrency(transaction.cogs_unit_cost)}</Typography>
                  </InfoBox>
                </Grid>
              )}
              {transaction.selling_price && (
                <Grid item xs={4}>
                  <InfoBox label={t('inventory.sellingPrice')}>
                    <Typography variant="body2">{formatCurrency(transaction.selling_price)}</Typography>
                  </InfoBox>
                </Grid>
              )}
            </Grid>
          </>
        )}

        {/* Meta section */}
        {(transaction.user_name || transaction.user_email || transaction.notes) && (
          <>
            <Divider sx={{ my: 2 }} />
            {(transaction.user_name || transaction.user_email) && (
              <Box sx={{ mb: transaction.notes ? 1.5 : 0 }}>
                <Typography variant="caption" color="text.secondary">{t('User')}</Typography>
                <Typography variant="body2">{transaction.user_name || transaction.user_email}</Typography>
              </Box>
            )}
            {transaction.notes && (
              <Box>
                <Typography variant="caption" color="text.secondary">{t('Notes')}</Typography>
                <Paper variant="outlined" sx={{ p: 1.5, mt: 0.5 }}>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                    {transaction.notes}
                  </Typography>
                </Paper>
              </Box>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('Close')}</Button>
      </DialogActions>
    </Dialog>
  );
}

// --- Transaction History Tab ---
function TransactionHistoryTab({ t, inventoryItem }) {
  const { currentOrganization } = useUserStore();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedTransaction, setSelectedTransaction] = useState(null);

  useEffect(() => {
    if (inventoryItem) {
      fetchTransactions();
    }
  }, [inventoryItem?.id]);

  const fetchTransactions = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await axios.get(
        `/api/inventory/${inventoryItem.id}/transactions/?organization=${currentOrganization.organization_id}`
      );
      const data = response.data;
      setTransactions(Array.isArray(data) ? data : data.results || []);
    } catch (err) {
      console.error('Failed to fetch transactions:', err);
      setError(t('Failed to load transaction history'));
    } finally {
      setLoading(false);
    }
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-';
    return new Intl.DateTimeFormat('fi-FI', {
      dateStyle: 'short',
      timeStyle: 'short',
    }).format(new Date(dateStr));
  };

  const formatCurrency = (value) => {
    if (value === null || value === undefined) return '-';
    return new Intl.NumberFormat('fi-FI', {
      style: 'currency',
      currency: 'EUR',
    }).format(value);
  };

  const getTransactionTypeLabel = (type) => {
    return t(`inventory.transactionTypes.${type}`, { defaultValue: type });
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  if (transactions.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <InventoryIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
        <Typography color="text.secondary">
          {t('inventory.noTransactions')}
        </Typography>
      </Box>
    );
  }

  if (isMobile) {
    return (
      <>
      <Stack spacing={1.5}>
        {transactions.map((txn) => {
          const config = transactionTypeConfig[txn.transaction_type] || { color: 'default' };
          const Icon = config.icon;
          const change = parseFloat(txn.quantity_change);

          return (
            <Card key={txn.id} variant="outlined" sx={{ cursor: 'pointer' }} onClick={() => setSelectedTransaction(txn)}>
              <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                <Box sx={{ mb: 1 }}>
                  <Chip
                    size="small"
                    label={getTransactionTypeLabel(txn.transaction_type)}
                    color={config.color}
                    icon={Icon ? <Icon /> : undefined}
                  />
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                    {formatDateTime(txn.created_at)}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 0.5 }}>
                  <Typography variant="body2" color="text.secondary">{t('Change')}</Typography>
                  <Typography
                    variant="body1"
                    sx={{ fontWeight: 600, color: change >= 0 ? 'success.main' : 'error.main' }}
                  >
                    {formatQuantityChange(txn.quantity_change)}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 0.5 }}>
                  <Typography variant="body2" color="text.secondary">{t('After')}</Typography>
                  <Typography variant="body2">{formatQuantity(txn.quantity_after)}</Typography>
                </Box>
                {txn.maintenance_id && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 0.5 }}>
                    <Typography variant="body2" color="text.secondary">{t('inventory.maintenanceLink')}</Typography>
                    <Link
                      component={RouterLink}
                      to={`/maintenances/${txn.maintenance_id}`}
                      sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
                    >
                      {txn.maintenance_work_number}
                      <LaunchIcon sx={{ fontSize: 14 }} />
                    </Link>
                  </Box>
                )}
                {!txn.maintenance_id && txn.reference_number && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 0.5 }}>
                    <Typography variant="body2" color="text.secondary">{t('inventory.maintenanceLink')}</Typography>
                    <Typography variant="body2">{txn.reference_number}</Typography>
                  </Box>
                )}
                {txn.transaction_type === 'purchase' && txn.unit_cost && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 0.5 }}>
                    <Typography variant="body2" color="text.secondary">{t('inventory.purchaseCost')}</Typography>
                    <Typography variant="body2">{formatCurrency(txn.unit_cost)}</Typography>
                  </Box>
                )}
                {txn.transaction_type === 'sale' && txn.selling_price && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 0.5 }}>
                    <Typography variant="body2" color="text.secondary">{t('inventory.sellingPrice')}</Typography>
                    <Typography variant="body2">{formatCurrency(txn.selling_price)}</Typography>
                  </Box>
                )}
                {(txn.user_name || txn.user_email) && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 0.5 }}>
                    <Typography variant="body2" color="text.secondary">{t('User')}</Typography>
                    <Typography variant="body2">{txn.user_name || txn.user_email}</Typography>
                  </Box>
                )}
                {txn.notes && (
                  <>
                    <Divider sx={{ my: 0.5 }} />
                    <Typography variant="body2" color="text.secondary">
                      {txn.notes}
                    </Typography>
                  </>
                )}
              </CardContent>
            </Card>
          );
        })}
      </Stack>
      <TransactionDetailDialog
        t={t}
        open={selectedTransaction !== null}
        onClose={() => setSelectedTransaction(null)}
        transaction={selectedTransaction}
      />
      </>
    );
  }

  return (
    <>
    <TableContainer component={Paper} variant="outlined">
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>{t('Date')}</TableCell>
            <TableCell>{t('Type')}</TableCell>
            <TableCell align="right">{t('Change')}</TableCell>
            <TableCell align="right">{t('After')}</TableCell>
            <TableCell>{t('inventory.maintenanceLink')}</TableCell>
            <TableCell align="right">{t('inventory.purchaseCost')} ({t('excl. VAT')})</TableCell>
            <TableCell align="right">{t('inventory.sellingPrice')}</TableCell>
            <TableCell>{t('User')}</TableCell>
            <TableCell>{t('Notes')}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {transactions.map((txn) => {
            const config = transactionTypeConfig[txn.transaction_type] || { color: 'default' };
            const Icon = config.icon;
            const change = parseFloat(txn.quantity_change);

            return (
              <TableRow key={txn.id} hover sx={{ cursor: 'pointer' }} onClick={() => setSelectedTransaction(txn)}>
                <TableCell>
                  <Typography variant="body2">
                    {formatDateTime(txn.created_at)}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={getTransactionTypeLabel(txn.transaction_type)}
                    color={config.color}
                    icon={Icon ? <Icon /> : undefined}
                  />
                </TableCell>
                <TableCell align="right">
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 600,
                      color: change >= 0 ? 'success.main' : 'error.main',
                    }}
                  >
                    {formatQuantityChange(txn.quantity_change)}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography variant="body2">
                    {formatQuantity(txn.quantity_after)}
                  </Typography>
                </TableCell>
                <TableCell>
                  {txn.maintenance_id ? (
                    <Link
                      component={RouterLink}
                      to={`/maintenances/${txn.maintenance_id}`}
                      sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
                    >
                      {txn.maintenance_work_number}
                      <LaunchIcon sx={{ fontSize: 14 }} />
                    </Link>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      {txn.reference_number || '-'}
                    </Typography>
                  )}
                </TableCell>
                <TableCell align="right">
                  <Typography variant="body2" color="text.secondary">
                    {txn.transaction_type === 'purchase' && txn.unit_cost
                      ? formatCurrency(txn.unit_cost)
                      : '-'}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography variant="body2" color="text.secondary">
                    {txn.transaction_type === 'sale' && txn.selling_price
                      ? formatCurrency(txn.selling_price)
                      : '-'}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">
                    {txn.user_name || txn.user_email || '-'}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      maxWidth: 200,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                    title={txn.notes}
                  >
                    {txn.notes || '-'}
                  </Typography>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
    <TransactionDetailDialog
      t={t}
      open={selectedTransaction !== null}
      onClose={() => setSelectedTransaction(null)}
      transaction={selectedTransaction}
    />
    </>
  );
}

// --- Main Dialog ---
function InventoryItemDialog({ t, open, onClose, inventoryItem, onSuccess, initialTab = 0, onEditPart }) {
  const [tab, setTab] = useState(initialTab);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Reset tab when dialog opens or initialTab changes
  useEffect(() => {
    if (open) {
      setTab(initialTab);
    }
  }, [open, initialTab]);

  const handleSettingsSaved = () => {
    setSnackbar({
      open: true,
      message: t('inventory.settingsSaved'),
      severity: 'success',
    });
    if (onSuccess) {
      onSuccess();
    }
  };

  if (!inventoryItem) return null;

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="lg"
        fullWidth
        data-testid="inventory-item-dialog"
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 0 }}>
          <Box>
            {inventoryItem.part_name}
            {(inventoryItem.part_primary_code || inventoryItem.part_manufacturer_code) && (
              <Typography variant="body2" color="text.secondary">
                {inventoryItem.part_primary_code || inventoryItem.part_manufacturer_code}
              </Typography>
            )}
          </Box>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}>
          <Tabs value={tab} onChange={(e, v) => setTab(v)} variant="scrollable" scrollButtons="auto" allowScrollButtonsMobile>
            <Tab
              label={t('inventory.overview')}
              icon={<InfoOutlinedIcon />}
              iconPosition="start"
            />
            <Tab
              label={t('inventory.settings')}
              icon={<SettingsIcon />}
              iconPosition="start"
            />
            <Tab
              label={t('inventory.transactionHistory')}
              icon={<HistoryIcon />}
              iconPosition="start"
              data-testid="inventory-item-tab-history"
            />
          </Tabs>
        </Box>
        <DialogContent>
          <TabPanel value={tab} index={0}>
            <OverviewTab t={t} inventoryItem={inventoryItem} />
          </TabPanel>
          <TabPanel value={tab} index={1}>
            <SettingsTab t={t} inventoryItem={inventoryItem} onSaved={handleSettingsSaved} />
          </TabPanel>
          <TabPanel value={tab} index={2}>
            <TransactionHistoryTab t={t} inventoryItem={inventoryItem} />
          </TabPanel>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'space-between' }}>
          {onEditPart ? (
            <Button onClick={onEditPart} startIcon={<EditIcon />}>
              {t('Edit Part')}
            </Button>
          ) : (
            <Box />
          )}
          <Button onClick={onClose}>{t('Close')}</Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}

export default withTranslation()(InventoryItemDialog);
